import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Button as MuiButton,
  IconButton,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { spacing, Box } from "@mui/system";
import { Print } from "@mui/icons-material";
import useAppSelector from "../../../hooks/useAppSelector";
import useAppDispatch from "../../../hooks/useAppDispatch";

import { fetchAssociation } from "../../../redux/slices/association";
import useAuth from "../../../hooks/useAuth";
import { format } from "date-fns";
import { fetchUsers } from "../../../redux/slices/user";
import { fetchProperties } from "../../../redux/slices/property";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Button = styled(MuiButton)(spacing);

const PrintableTable = styled(Table)`
  @media print {
    width: 100% !important;
    & .MuiTableCell-root {
      border: 1px solid #000 !important;
      font-size: 10px !important;
      padding: 4px !important;
    }
    & .MuiTableHead-root .MuiTableCell-root {
      background-color: #f5f5f5 !important;
      font-weight: bold !important;
    }
  }
`;

const PrintableContainer = styled("div")`
  @media print {
    & .no-print {
      display: none !important;
    }
    & .MuiBreadcrumbs-root {
      display: none !important;
    }
    & .MuiDivider-root {
      display: none !important;
    }
  }
`;

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface PropertyUnitization {
  id: number;
  tonKm: number;
  weightTons: number;
  notes?: string;
}

interface PropertyUsageFee {
  id: number;
  amountCents: number;
  notes?: string;
}

interface Property {
  user: { firstName: string; lastName: string };
  code: string;
  name: string;
  units: number | string; // deprecated - now using propertyUnitizations
  propertyUnitizations?: PropertyUnitization[];
  propertyUsageFees?: PropertyUsageFee[];
}

// helpers for units formatting
const toNum = (v: number | string | null | undefined): number =>
  v == null || v === "" ? 0 : typeof v === "number" ? v : Number(v);

const fmt3 = (v: number | string) =>
  toNum(v).toLocaleString("fi-FI", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

function UnitList(props: any) {
  const groupedProperties: { [ownerName: string]: Property[] } = {};

  // Use properties from the separate fetch which includes propertyUnitizations and propertyUsageFees
  props.properties?.forEach((property: Property) => {
    const ownerName = `${property.user.firstName} ${property.user.lastName}`;
    if (!groupedProperties[ownerName]) {
      groupedProperties[ownerName] = [];
    }
    groupedProperties[ownerName].push(property);
  });

  // Grand totals
  let grandTotalUnits = 0;
  let grandTotalUnitPay = 0;
  let grandTotalUsageFees = 0;
  let grandTotalBasePay = 0;
  let grandTotalAmount = 0;

  const handlePrint = () => {
    window.print();
  };

  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            {props.association.name} - Maksuunpanoluettelo
          </Typography>
          <IconButton
            onClick={handlePrint}
            title="Tulosta"
            className="no-print"
          >
            <Print />
          </IconButton>
        </Box>

        <table>
          <thead></thead>
          <tbody>
            <tr>
              <td>
                {format(new Date(props.association.meetingDate), "dd.MM.yyyy")}{" "}
                Vuosikokouspäätös{" "}
                {format(new Date(props.association.meetingDate), "yyyy")}.
                eräpäivä{" "}
                {format(new Date(props.association.billDueDate), "dd.MM.yyyy")},
                muistutusmaksu{" "}
                {new Intl.NumberFormat("fi-FI", {
                  style: "currency",
                  currency: "EUR",
                }).format(props.association.reminderFee)}
                , ulosottokulumaksu{" "}
                {new Intl.NumberFormat("fi-FI", {
                  style: "currency",
                  currency: "EUR",
                }).format(props.association.foreclosureFee)}
                .
              </td>
            </tr>
            <tr>
              <td>
                Maksajia {props.users.length} kpl, kiinteistöjä{" "}
                {props.properties.length} kpl
              </td>
            </tr>
            <tr>
              <td>Yksikköhinta {props.association.unitPay} €</td>
            </tr>
            <tr>
              <td>
                Perusmaksu{" "}
                {new Intl.NumberFormat("fi-FI", {
                  style: "currency",
                  currency: "EUR",
                }).format(props.association.basePay)}{" "}
                / maksaja
              </td>
            </tr>
          </tbody>
        </table>
      </CardContent>
      <Paper>
        <PrintableTable size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell>Omistaja</TableCell>
              <TableCell>Kiinteistö</TableCell>
              <TableCell align="right">Yksiköt (tKm)</TableCell>
              <TableCell align="right">Yksikkömaksu</TableCell>
              <TableCell align="right">Käyttömaksut</TableCell>
              <TableCell align="right">Perusmaksu</TableCell>
              <TableCell align="right">Yhteensä</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(groupedProperties).map(
              ([ownerName, userProperties], userIndex) => {
                let userTotalUnits = 0;
                let userTotalUnitPay = 0;
                let userTotalUsageFees = 0;
                const basePay = props.association.basePay; // Base pay once per user

                const userRows = userProperties.map((property, propIndex) => {
                  // Calculate units from propertyUnitizations (sum of tonKm)
                  // Fallback to old property.units if no unitizations exist
                  let units = 0;
                  if (
                    property.propertyUnitizations &&
                    property.propertyUnitizations.length > 0
                  ) {
                    units = property.propertyUnitizations.reduce(
                      (sum, unit) => sum + (unit.tonKm || 0),
                      0
                    );
                  } else {
                    // Fallback to old units field for backward compatibility
                    units = toNum(property.units);
                  }

                  const unitPay = props.association.unitPay * units;

                  // Calculate usage fees (convert from cents to euros)
                  const usageFees = property.propertyUsageFees
                    ? property.propertyUsageFees.reduce(
                        (sum, fee) => sum + (fee.amountCents || 0) / 100,
                        0
                      )
                    : 0;

                  // Update user totals
                  userTotalUnits += units;
                  userTotalUnitPay += unitPay;
                  userTotalUsageFees += usageFees;

                  return (
                    <TableRow key={`${userIndex}-${propIndex}`}>
                      <TableCell>{ownerName}</TableCell>
                      <TableCell>{`${property.code} (${property.name})`}</TableCell>
                      <TableCell align="right">{fmt3(units)}</TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat("fi-FI", {
                          style: "currency",
                          currency: "EUR",
                        }).format(unitPay)}
                      </TableCell>
                      <TableCell align="right">
                        {usageFees > 0
                          ? new Intl.NumberFormat("fi-FI", {
                              style: "currency",
                              currency: "EUR",
                            }).format(usageFees)
                          : "-"}
                      </TableCell>
                      <TableCell align="right">-</TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat("fi-FI", {
                          style: "currency",
                          currency: "EUR",
                        }).format(unitPay + usageFees)}
                      </TableCell>
                    </TableRow>
                  );
                });

                // Update grand totals AFTER user summary row
                grandTotalUnits += userTotalUnits;
                grandTotalUnitPay += userTotalUnitPay;
                grandTotalUsageFees += userTotalUsageFees;
                grandTotalBasePay += basePay;
                grandTotalAmount +=
                  userTotalUnitPay + userTotalUsageFees + basePay;

                return (
                  <React.Fragment key={userIndex}>
                    {userRows}
                    {/* User Summary Row */}
                    <TableRow
                      style={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}
                    >
                      <TableCell>Yhteensä - {ownerName}</TableCell>
                      <TableCell></TableCell>
                      <TableCell align="right">
                        {fmt3(userTotalUnits)}
                      </TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat("fi-FI", {
                          style: "currency",
                          currency: "EUR",
                        }).format(userTotalUnitPay)}
                      </TableCell>
                      <TableCell align="right">
                        {userTotalUsageFees > 0
                          ? new Intl.NumberFormat("fi-FI", {
                              style: "currency",
                              currency: "EUR",
                            }).format(userTotalUsageFees)
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat("fi-FI", {
                          style: "currency",
                          currency: "EUR",
                        }).format(basePay)}
                      </TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat("fi-FI", {
                          style: "currency",
                          currency: "EUR",
                        }).format(
                          userTotalUnitPay + userTotalUsageFees + basePay
                        )}
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              }
            )}

            {/* Grand Summary Row */}
            <TableRow
              style={{ fontWeight: "bold", backgroundColor: "#d0d0d0" }}
            >
              <TableCell>Kaikki yhteensä</TableCell>
              <TableCell></TableCell>
              <TableCell align="right">{fmt3(grandTotalUnits)}</TableCell>
              <TableCell align="right">
                {new Intl.NumberFormat("fi-FI", {
                  style: "currency",
                  currency: "EUR",
                }).format(grandTotalUnitPay)}
              </TableCell>
              <TableCell align="right">
                {grandTotalUsageFees > 0
                  ? new Intl.NumberFormat("fi-FI", {
                      style: "currency",
                      currency: "EUR",
                    }).format(grandTotalUsageFees)
                  : "-"}
              </TableCell>
              <TableCell align="right">
                {new Intl.NumberFormat("fi-FI", {
                  style: "currency",
                  currency: "EUR",
                }).format(grandTotalBasePay)}
              </TableCell>
              <TableCell align="right">
                {new Intl.NumberFormat("fi-FI", {
                  style: "currency",
                  currency: "EUR",
                }).format(grandTotalAmount)}
              </TableCell>
            </TableRow>
          </TableBody>
        </PrintableTable>
      </Paper>
    </Card>
  );
}

function Units() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { association } = useAppSelector((state) => state.association);
  const { users } = useAppSelector((state) => state.user);
  const { properties } = useAppSelector((state) => state.property);

  const [open, setOpen] = useState(false);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  useEffect(() => {
    dispatch(fetchAssociation(associationId));
    dispatch(fetchUsers(associationId));
    dispatch(fetchProperties(associationId));
  }, [associationId, dispatch]);

  return (
    <PrintableContainer>
      <Helmet title="Tiet" />
      <Typography
        variant="h3"
        gutterBottom
        display="inline"
        className="no-print"
      >
        Yksiköinti
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2} className="no-print">
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/">
          Yksiköinti
        </Link>
        <Typography>Maksuunpanoluettelo</Typography>
      </Breadcrumbs>

      <Divider my={6} className="no-print" />

      {association && users && properties && (
        <UnitList
          association={association}
          users={users}
          properties={properties}
          openForm={open}
        />
      )}
    </PrintableContainer>
  );
}

export default Units;
